// we want the yaml generated to be in the order we describe
/* eslint-disable sort-keys-fix/sort-keys-fix */

import { platformListTables } from 'shared/base';
import { fetchAllPages } from 'shared/base/utils/api';

import { ReportYamlItem } from '@/components/reports/types/report';

const emptyQuery = `
  SELECT '' as cloud, '' as account, '' as region, '' as name, 0 as usage, 0 as limit, 0 as usage_percentage limit 0
`;

const tablesToSelect = [
  {
    alias: '_azure_quota_usage_percentages',
    select: `
        _azure_quota_limits AS (
            SELECT
                _cq_account as account,
                name,
                JSONExtractString(properties, 'name', 'localizedValue') as display_name,
                JSONExtractInt(properties, 'limit', 'value') as limit,
                arrayElementOrNull(extractGroups(coalesce(id, ''), '/locations/(.+?)/'), 1) AS region
            FROM azure_quota_quotas
            WHERE limit > 0 AND JSONExtractBool(properties, 'isQuotaApplicable')
        ),
        _azure_quota_usages AS (
            SELECT
                _cq_cloud as cloud,
                _cq_account as account,
                name,
                _cq_account_name as account_name,
                JSONExtractString(properties, 'name', 'localizedValue') as display_name,
                JSONExtractInt(properties, 'usages', 'value') as usage,
                arrayElementOrNull(extractGroups(coalesce(id, ''), '/locations/(.+?)/'), 1) AS region
            FROM azure_quota_usages
            WHERE usage > 0 AND JSONExtractBool(properties, 'isQuotaApplicable')
        ),
        _azure_quota_usage_percentages AS (
            SELECT
                qu.cloud as cloud,
                qu.display_name as name,
                COALESCE(NULLIF(qu.account_name, ''), qu.account) as account,
                qu.region as region,
                toFloat64(qu.usage) as usage,
                toFloat64(ql.limit) as limit,
                qu.usage/ql.limit * 100 as usage_percentage
            FROM _azure_quota_usages AS qu
            INNER JOIN _azure_quota_limits AS ql ON qu.name = ql.name AND qu.region = ql.region AND qu.account = ql.account
        )
    `,
    tables: ['azure_quota_quotas', 'azure_quota_usages'],
  },
  {
    alias: '_gcp_usage_percentages',
    select: `
        _gcp_usage_percentages AS (
            SELECT
                _cq_cloud as cloud,
                COALESCE(NULLIF(_cq_account_name, ''), _cq_account) as account,
                _cq_region as region,
                toFloat64(quota.limit) AS limit,
                toFloat64(quota.usage) AS usage,
                initcapUTF8(replaceAll(quota.metric, '_', ' ')) AS name,
                quota.usage/quota.limit * 100 as usage_percentage,
                JSONExtract(assumeNotNull(quotas), 'Array(Tuple(usage Int32, limit Int32, metric String))') as qs
            FROM gcp_compute_projects
            ARRAY JOIN qs as quota
        )

    `,
    tables: ['gcp_compute_projects'],
  },
  {
    alias: '_aws_usage_percentages',
    select: `
        _aws_latest_usages AS (
            SELECT
                MAX(timestamp) AS ts,
                _cq_parent_id AS id
            FROM aws_servicequotas_quota_utilizations
            GROUP BY _cq_parent_id
        ),
        _aws_usage_percentages AS (
            SELECT
            asqu._cq_cloud as cloud,
            COALESCE(NULLIF(asqu._cq_account_name, ''), asqu._cq_account) as account,
            asqu._cq_region as region,
            asq.quota_name as name,
            (asqu.usage_percentage/100 * toFloat64(asq.value)) as usage,
            toFloat64(asq.value) as limit,
            asqu.usage_percentage as usage_percentage
            FROM aws_servicequotas_quota_utilizations asqu
            JOIN _aws_latest_usages alu ON alu.ts = asqu.timestamp
                            AND alu.id = asqu._cq_parent_id
            JOIN aws_servicequotas_quotas asq ON alu.id = asq._cq_id
        )
    `,
    tables: ['aws_servicequotas_quota_utilizations', 'aws_servicequotas_quotas'],
  },
];

const getReportQuery = (tables: string[]) => {
  const filteredTables = tablesToSelect.filter((t) =>
    t.tables.every((table) => tables.includes(table)),
  );
  if (filteredTables.length === 0) {
    return emptyQuery;
  }

  const withQuery = `WITH ${filteredTables.map((t) => t.select).join(', \n')}`;

  return `
    ${withQuery}
        SELECT cloud, account, region, name, usage, limit, usage_percentage FROM (
        ${filteredTables
          .map(
            (t) =>
              `SELECT cloud, account, region, name, usage, limit, usage_percentage FROM ${t.alias}`,
          )
          .join('\nUNION ALL\n')}
    )
  `;
};

export async function getReportItems() {
  const data = await fetchAllPages((page) => platformListTables({ page, per_page: 1000 }));
  const allTables = data?.data?.items?.map((item: { name: string }) => item.name) ?? [];
  const baseQuery = getReportQuery(allTables);

  const widgets: ReportYamlItem[] = [
    {
      title: 'Service Quota & Limits Report',
      display: {
        type: 'table',
      },
      query: `
    select cloud, account, region, name, usage, limit, usage_percentage
    from (
      ${baseQuery}
    )
    where 1=1 and usage_percentage >= ?
    order by usage_percentage desc
      `,
    },
  ];

  return { widgets };
}
